import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { loginSchema, type LoginInput } from "../schemas";
import { useLogin } from "../hooks";
import { authApi } from "../api";
import { useQuery } from "@tanstack/react-query";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  // Invite-only platforms hide the sign-up link entirely.
  const configQ = useQuery({
    queryKey: ["auth", "config"],
    queryFn: authApi.config,
    staleTime: 10 * 60_000,
  });

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "", mfa_code: "" },
  });

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ||
    "/dashboard";

  async function onSubmit(values: LoginInput) {
    try {
      const result = await login.mutateAsync(values);
      if (result?.mfa_required && !values.mfa_code) {
        setMfaRequired(true);
        toast.info("Enter your 6-digit verification code");
        return;
      }
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      type BackendErr = {
        detail?: string;
        error?: string | { code?: string; message?: string };
      };
      const data = (err as AxiosError<BackendErr>)?.response?.data;
      const rawError = data?.error;
      const message =
        data?.detail ||
        (typeof rawError === "string" ? rawError : rawError?.message) ||
        (err as Error)?.message ||
        "Invalid email or password";
      toast.error(message);
    }
  }

  return (
    <div className="w-full max-w-sm space-y-8">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-sm text-muted-foreground">
          Enter your credentials to access your workspace
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Password</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Forgot?
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {mfaRequired && (
            <FormField
              control={form.control}
              name="mfa_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verification code</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={login.isPending || form.formState.isSubmitting}
          >
            {(login.isPending || form.formState.isSubmitting) && (
              <Loader2 className="size-4 animate-spin" />
            )}
            Sign in
          </Button>
        </form>
      </Form>

      {configQ.data?.public_signup_enabled && (
        <p className="text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link to="/register" className="text-foreground hover:underline">
            Create one
          </Link>
        </p>
      )}
    </div>
  );
}
